import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "@/lib/scroll";

const ABOUT_PLATE_IMG = "/images/about-plate.webp";
const ABOUT_JAR_IMG = "/images/recipe-jar.webp";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      id="despre"
      ref={ref}
      className="relative overflow-hidden border-y border-gold/10 bg-[#141311] py-24 lg:py-36"
    >
      <div className="pointer-events-none absolute left-[-15%] top-1/3 h-[50vh] w-[50vw] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.07)_0%,rgba(17,17,17,0)_70%)]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-gold/70" />
              <p className="font-mono text-[10px] uppercase tracking-[0.38em] text-gold sm:text-[11px]">
                Despre NOSA
              </p>
            </div>
            <blockquote className="font-serif text-3xl font-light leading-[1.2] tracking-tight text-cream sm:text-4xl lg:text-[2.9rem]">
              „NOSA a luat naștere din dorința sinceră de a oferi felinelor o{" "}
              <em className="text-gold-gradient">nutriție simplă, curată și super premium</em>,
              folosind doar ingrediente alese cu cel mai mare discernământ.”
            </blockquote>
            <p className="mt-10 max-w-lg text-sm leading-relaxed text-stone lg:text-base">
              Fiecare rețetă NOSA reflectă respectul absolut pentru anatomia felină. În locul
              compromisurilor din industria de masă, am creat o simfonie nutritivă în sticlă:
              carne proaspătă, dovleac și micronutrienți esențiali care susțin o viață lungă
              și armonioasă.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <img
                src="/images/logo-mark.png"
                alt="NOSA Pet Food"
                className="h-10 w-auto object-contain"
                data-testid="about-logo-mark"
              />
              <span className="h-px w-16 bg-gold/40" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-stone/70">
                Pet Food · România
              </span>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: 48 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-0 -translate-x-5 translate-y-5 rounded-3xl border border-gold/25" aria-hidden="true" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-gold/40">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={ABOUT_PLATE_IMG}
                alt="Farfurie cu hrană NOSA pe tăviță de lemn, cu pui proaspăt, dovleac și pătrunjel"
                className="h-full w-full object-cover"
                data-testid="about-plate-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
              className="absolute -bottom-8 -left-4 w-40 overflow-hidden rounded-2xl border border-gold/40 shadow-[0_18px_50px_rgba(0,0,0,0.55)] sm:-left-10 sm:w-48"
            >
              <img
                src={ABOUT_JAR_IMG}
                alt="Borcan NOSA „Pui și Dovleac”, 85 g"
                className="aspect-square h-full w-full object-cover"
                data-testid="about-jar-image"
              />
            </motion.div>
            <div className="absolute -bottom-6 right-8 rounded-2xl border border-gold/30 bg-card/95 px-6 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl">
              <p className="font-mono text-[9px] uppercase tracking-[0.26em] text-stone">
                Simplu · Natural · Premium
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
